import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { AxiosError } from 'axios';

import { useTurnstile } from '@/hooks/useTurnstile';
import i18n from '@/settings/i18n';
import { sentryReportError } from '@/utils/sentryReportError';
import { pages } from '@/settings/pages';
import { adminApi } from '@/services/api';
import { useFlashMessenger } from '@/hooks/useFlashMessenger';
import { useLocaleFromUrl } from '@/hooks/useLocaleFromUrl';

import { ActivationStart, Values } from '../Presentation';

export const ActivationStartContainer: React.FC = () => {
  const { flashError } = useFlashMessenger();

  // 送信中フラグ（二重送信防止）
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Turnstile関連
  const {
    isLoading: turnstileLoading,
    turnstileError,
    onSuccess: onTurnstileSuccess,
    onError: onTurnstileError,
    resetError: resetTurnstileError,
    createParamsWithTurnstile,
  } = useTurnstile();

  const navigate = useNavigate();
  const location = useLocation();
  useLocaleFromUrl();

  const initialEmail = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('email') || '';
  }, [location.search]);

  const onSubmit = useCallback(
    async (values: Values) => {
      try {
        setIsSubmitting(true);
        resetTurnstileError();

        const result = createParamsWithTurnstile(values);
        if (!result.isValid) {
          setIsSubmitting(false);
          return;
        }
        const data = result.data;

        await adminApi.activateAccountMail({ data });
        navigate(pages.token.sent);
      } catch (err) {
        if (err instanceof AxiosError && err.response !== undefined) {
          // 送信ボタン再活性化（再送信可能にする）
          setIsSubmitting(false);

          if (err.response && err.response.data.error === 'already_activated') {
            navigate(pages.activation.alreadyCompleted);
            return;
          }

          // 4xx, 5xx → 予期せぬエラーメッセージを表示
          sentryReportError(err);
          flashError(i18n.t('ui.error.unexpected'));
        }
      }
    },
    [createParamsWithTurnstile, resetTurnstileError, flashError, navigate],
  );

  const childProps = {
    initialEmail,
    isDisabled: isSubmitting,
    onSubmit,
    turnstileError,
    turnstileLoading, // FormContents で ignoreTurnstile との組み合わせ判定に使用
    onTurnstileSuccess,
    onTurnstileError,
  };
  return <ActivationStart {...childProps} />;
};
